import type { FieldDefinitionDto, ObjectType } from '@crm/shared';

/** snake_case field key → API property name for system fields that are real columns. */
export const SYSTEM_PROPS: Record<ObjectType, Record<string, string>> = {
  CONTACT: {
    first_name: 'firstName',
    last_name: 'lastName',
    email: 'email',
    phone: 'phone',
    mobile: 'mobile',
    job_title: 'jobTitle',
    organization_id: 'organizationId',
    lifecycle_stage: 'lifecycleStage',
    lead_status: 'leadStatus',
    source: 'source',
    email_status: 'emailStatus',
    subscribed: 'subscribed',
    created_at: 'createdAt',
    updated_at: 'updatedAt',
  },
  ORGANIZATION: {
    name: 'name',
    domain: 'domain',
    website: 'website',
    industry: 'industry',
    type: 'type',
    employees: 'employees',
    annual_revenue: 'annualRevenue',
    phone: 'phone',
    created_at: 'createdAt',
    updated_at: 'updatedAt',
  },
  DEAL: {
    title: 'title',
    pipeline_id: 'pipelineId',
    stage_id: 'stageId',
    value: 'value',
    currency: 'currency',
    contact_id: 'contactId',
    organization_id: 'organizationId',
    agent_id: 'agentId',
    expected_close_at: 'expectedCloseAt',
    source: 'source',
    description: 'description',
    lost_reason: 'lostReason',
    created_at: 'createdAt',
    updated_at: 'updatedAt',
  },
};

export const READ_ONLY_KEYS = new Set(['email_status', 'created_at', 'updated_at']);
/** Keys rendered with a dedicated picker instead of a plain input. */
export const RELATION_KEYS = new Set(['organization_id', 'contact_id', 'pipeline_id', 'stage_id', 'agent_id']);

export function isEditable(def: FieldDefinitionDto) {
  return !READ_ONLY_KEYS.has(def.key);
}

/**
 * Fields that can go on the quick create modal: every editable one except contact mobile (it is a phone label)
 * and deal pipeline/stage (the modal always shows those) and lost reason.
 */
const NO_QUICK_ADD: Record<ObjectType, string[]> = { CONTACT: ['mobile'], ORGANIZATION: [], DEAL: ['pipeline_id', 'stage_id', 'lost_reason'] };
export function canQuickAdd(def: FieldDefinitionDto) {
  return isEditable(def) && !NO_QUICK_ADD[def.objectType].includes(def.key);
}

/** The fields shown on the quick create modal, in form order: the ones picked in Settings → Fields plus every required one. */
export function quickAddFields(defs: FieldDefinitionDto[]) {
  return defs.filter((d) => canQuickAdd(d) && (d.quickAdd || d.required));
}

/** Reads a field's value from an API record (system column or custom JSON). */
export function readValue(def: FieldDefinitionDto, record: Record<string, unknown>): unknown {
  if (def.isSystem) {
    const prop = SYSTEM_PROPS[def.objectType][def.key] ?? def.key;
    return record[prop];
  }
  return (record.fields as Record<string, unknown> | undefined)?.[def.key];
}
